import { Delta } from 'quill';
import { Note } from '../../../_service/data.service';
import { NoteJsonExportRecord } from '../../../_service/note-print-export.service';

export type ImportRowStatus = 'new' | 'conflict' | 'invalid';
export type ImportDecision = 'import' | 'overwrite' | 'skip';

export interface ImportPreviewRow {
  /** Stable identity for the dialog's rows — the record's own _id when it has
   * one, else its position in the file (so an invalid/missing-_id record can
   * still be listed and tracked). */
  key: string;
  record: NoteJsonExportRecord;
  status: ImportRowStatus;
  errors: string[];
  /** 'new' rows are always 'import'; 'invalid' rows are always 'skip'; only
   * 'conflict' rows are user-editable between 'overwrite' and 'skip'. */
  decision: ImportDecision;
}

/**
 * Stateless logic for "Import JSON Note(s)..." extracted from HomeComponent,
 * mirroring the other home/logic/*.logic.ts helpers. Parses/validates the
 * file produced by NotePrintExportService.exportNotesToJson() and builds the
 * Note object to persist for each row the user chooses to import.
 */
export class NoteJsonImportLogic {
  /** Parse the raw file text into a flat list of note records. Returns
   * file-level errors (unparseable JSON, unsupported version, no notes)
   * separately from per-record validation, which happens per row instead —
   * one bad record should not block the rest of a batch. */
  static parseImportFile(text: string): { records: unknown[]; fileErrors: string[] } {
    let parsed: any;
    try {
      parsed = JSON.parse(text);
    } catch {
      return { records: [], fileErrors: ['The selected file is not valid JSON.'] };
    }

    if (!parsed || typeof parsed !== 'object') {
      return { records: [], fileErrors: ['The selected file does not contain a valid note export.'] };
    }

    if (parsed.exportFormatVersion !== undefined && parsed.exportFormatVersion !== 1) {
      return {
        records: [],
        fileErrors: [`Unsupported export format version: ${JSON.stringify(parsed.exportFormatVersion)}.`],
      };
    }

    const notes = Array.isArray(parsed.notes) ? parsed.notes : Array.isArray(parsed) ? parsed : null;
    if (!notes || !notes.length) {
      return { records: [], fileErrors: ['The selected file does not contain any notes to import.'] };
    }

    return { records: notes, fileErrors: [] };
  }

  /** Validates one raw record against the mandatory fields a Note requires.
   * Returns an empty array when valid. */
  static validateRecord(record: unknown): string[] {
    const errors: string[] = [];
    if (!record || typeof record !== 'object') {
      return ['Not a valid note record.'];
    }
    const r = record as Record<string, unknown>;

    if (typeof r['_id'] !== 'string' || !(r['_id'] as string).trim()) {
      errors.push('Missing "_id".');
    } else if (!/^(note|template):/.test(r['_id'] as string)) {
      errors.push('"_id" must start with "note:" or "template:".');
    }
    if (r['type'] !== 'note' && r['type'] !== 'template') {
      errors.push('Missing or invalid "type" (must be "note" or "template").');
    }
    if (typeof r['Title'] !== 'string' || !(r['Title'] as string).trim()) {
      errors.push('Missing "Title".');
    }
    if (typeof r['NoteType'] !== 'string' || !(r['NoteType'] as string).trim()) {
      errors.push('Missing "NoteType".');
    }
    if (typeof r['Status'] !== 'string' || !(r['Status'] as string).trim()) {
      errors.push('Missing "Status".');
    }
    // NOTE: "Project" is intentionally NOT validated here — it's obsolete on
    // the backend (commented out of NoteCreateUpdateSchema; not part of
    // IMPORT_REQUIRED_STRING_FIELDS) and many current notes no longer carry
    // it. The only mandatory import fields are _id/type/Title/NoteType/Status.
    return errors;
  }

  /** Builds the preview rows for a parsed batch: validates each record,
   * flags conflicts against already-known local ids, and flags a second
   * occurrence of the same _id within the file itself (the first occurrence
   * wins; the repeat is invalid) — the "no duplication of _id's" guarantee
   * needs to hold within a single import, not just against existing notes. */
  static buildPreviewRows(records: unknown[], existingIdSet: ReadonlySet<string>): ImportPreviewRow[] {
    const seenInFile = new Set<string>();

    return records.map((raw, index) => {
      const record = raw as NoteJsonExportRecord;
      const id = typeof (raw as any)?._id === 'string' ? (raw as any)._id : undefined;
      const errors = this.validateRecord(raw);

      if (id && errors.length === 0) {
        if (seenInFile.has(id)) {
          errors.push('Duplicate "_id" within this import file — an earlier note in the file already uses it.');
        } else {
          seenInFile.add(id);
        }
      }

      const key = id || `#${index}`;

      if (errors.length > 0) {
        return { key, record, status: 'invalid', errors, decision: 'skip' };
      }

      const isConflict = existingIdSet.has(id!);
      return {
        key,
        record,
        status: isConflict ? 'conflict' : 'new',
        errors: [],
        decision: isConflict ? 'skip' : 'import', // conservative default: don't overwrite without an explicit choice
      };
    });
  }

  /** Builds the Note to persist for one row the user chose to import/overwrite.
   * Fields the export format never carries (Tags, RelatedNotes, Attachments,
   * AiKeywords, ai_summary, the legacy Delta formats) are taken from the
   * existing local note when overwriting (so importing never wipes them),
   * or defaulted empty for a brand-new note. */
  static buildNoteFromRecord(record: NoteJsonExportRecord, existing: Note | undefined): Note {
    const now = new Date().toISOString();

    const note: Note = {
      ...(existing ?? ({} as Note)),
      _id: record._id,
      type: record.type,
      NoteType: record.NoteType,
      Title: record.Title,
      // Project/ProjectName are obsolete on the backend and may be absent
      // from the record entirely — keep the existing note's value on
      // overwrite, or default to 0/'' for a brand-new note, rather than
      // writing `undefined` into a field the Note model still declares
      // non-optional.
      Project: typeof record.Project === 'number' ? record.Project : (existing?.Project ?? 0),
      ProjectName: record.ProjectName ?? existing?.ProjectName ?? '',
      Status: record.Status,
      Confidential: !!record.Confidential,
      DescriptionHtml: record.DescriptionHtml ?? '',
      CommentsHtml: record.CommentsHtml ?? '',
      Keywords: record.Keywords ?? '',
      created_on: record.created_on || now,
      last_modified: now,
    };

    if (!existing) {
      // Brand-new note: fields the export never carries start empty rather
      // than undefined, matching DataOfflineService.createNote()'s defaults.
      note.DescriptionDelta = new Delta();
      note.CommentsDelta = new Delta();
      note.AiKeywords = '';
      note.Tags = [];
      note.RelatedNotes = [];
      note.Attachments = [];
      delete (note as any)._rev;
      delete (note as any).remote_rev;
      delete (note as any).ai_summary;
    }

    if (record.templateName) note.templateName = record.templateName;
    note.ReferenceDate = record.ReferenceDate || (existing ? existing.ReferenceDate : record.created_on || now);
    if (record.StartDate) note.StartDate = record.StartDate;
    if (record.EndDate) note.EndDate = record.EndDate;

    note.syncStatus = existing ? 'updated' : 'new';
    return note;
  }
}
